// Photo validation utility - mirrors the backend's real limits
// (server/middleware/uploadMiddleware.js + imageDimensionMiddleware.js) so
// users get the same feedback instantly instead of waiting for a server
// round-trip. The backend is still the source of truth and re-validates
// everything - this is just for a faster, friendlier UI.

export const PHOTO_CONSTRAINTS = {
  MAX_SIZE_MB: 15,
  MAX_SIZE_BYTES: 15 * 1024 * 1024,
  MIN_WIDTH: 200,
  MIN_HEIGHT: 200,
  // No hard max dimension client-side: the backend auto-resizes anything
  // over 1920px on the long edge rather than rejecting it, so large photos
  // from phone cameras are still fine to select.
  ALLOWED_FORMATS: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif', 'image/gif', 'image/tiff', 'image/bmp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.avif', '.gif', '.tif', '.tiff', '.bmp']
};

export async function validatePhoto(file) {
  const errors = [];

  // Check file exists
  if (!file) {
    return { valid: false, errors: ['No file selected'] };
  }

  // Check file size
  if (file.size > PHOTO_CONSTRAINTS.MAX_SIZE_BYTES) {
    errors.push(`File size exceeds ${PHOTO_CONSTRAINTS.MAX_SIZE_MB}MB limit (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
  }

  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
  const typeAllowed = PHOTO_CONSTRAINTS.ALLOWED_FORMATS.includes(file.type);
  const extAllowed = PHOTO_CONSTRAINTS.ALLOWED_EXTENSIONS.includes(ext);
  if (!typeAllowed && !extAllowed) {
    errors.push(`File type not supported. Allowed: common image formats (current: ${file.type || ext || 'unknown'})`);
  }


  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // Check image dimensions
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const imgErrors = [];
        if (img.width < PHOTO_CONSTRAINTS.MIN_WIDTH) {
          imgErrors.push(`Image width too small: ${img.width}px (minimum: ${PHOTO_CONSTRAINTS.MIN_WIDTH}px)`);
        }
        if (img.height < PHOTO_CONSTRAINTS.MIN_HEIGHT) {
          imgErrors.push(`Image height too small: ${img.height}px (minimum: ${PHOTO_CONSTRAINTS.MIN_HEIGHT}px)`);
        }

        resolve({
          valid: imgErrors.length === 0,
          errors: imgErrors,
          dimensions: { width: img.width, height: img.height }
        });
      };
      img.onerror = () => {
        resolve(isHeifPhoto(file)
          ? { valid: true, errors: [] } // The server validates formats this browser cannot decode.
          : { valid: false, errors: ['Invalid image file'] });
      };
      img.src = e.target.result;
    };
    reader.onerror = () => resolve({ valid: false, errors: ['Could not read image file'] });
    reader.onabort = reader.onerror;
    reader.readAsDataURL(file);
  });
}

function isHeifPhoto(file) {
  return /^image\/hei[cf]$/.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

export async function compressPhoto(file, targetQuality = 0.8) {
  return new Promise((resolve, reject) => {
    const fail = () => reject(new Error(`Could not convert "${file.name}" to JPEG. Try exporting it as JPEG and selecting it again.`));
    const reader = new FileReader();
    reader.onerror = fail;
    reader.onabort = fail;
    reader.onload = () => {
      const img = new Image();
      img.onerror = fail;
      img.onload = () => {
        try {
          const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) return fail();
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => {
            if (!blob || blob.type !== 'image/jpeg') return fail();
            resolve({
              compressed: true,
              originalSize: file.size,
              compressedSize: blob.size,
              reduction: ((1 - blob.size / file.size) * 100).toFixed(2),
              file: new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`, {
                type: 'image/jpeg', lastModified: file.lastModified
              })
            });
          }, 'image/jpeg', targetQuality);
        } catch (_) { fail(); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Build a fresh form so retries and callers retain their original files.
export async function normalizePhotoFormData(data) {
  const normalized = new FormData();
  for (const [key, value] of data.entries()) {
    if (!(value instanceof File)) {
      normalized.append(key, value);
      continue;
    }
    let photo;
    try {
      photo = (await compressPhoto(value)).file;
    } catch (error) {
      if (!isHeifPhoto(value)) throw error;
      // HEIC decoding is browser-dependent; retain server-side conversion.
      photo = new File([value], value.name, { type: 'image/heif', lastModified: value.lastModified });
    }
    if (photo.size > PHOTO_CONSTRAINTS.MAX_SIZE_BYTES) {
      throw new Error(`"${value.name}" exceeds the 8MB upload limit after conversion.`);
    }
    normalized.append(key, photo);
  }
  return normalized;
}

export function formatPhotoError(errors) {
  return errors.map(err => `• ${err}`).join('\n');
}
